/**
 * Verificación del markup de la tasa Aguacate contra el spot (HU-APP-038).
 *
 * El panel ya muestra el spot USD/CLP y, desde ahora, la tasa USD/CLP que
 * Aguacate aplica. Este archivo compara las dos y dice si el margen entre
 * ellas se pasó de lo aceptable, para que el operador lo vea sin tener que
 * dividir de cabeza.
 *
 * No se confunde con el vigilante de variación de [[rate-drift-watcher]]:
 * aquel mide cuánto **saltó** una tasa entre dos lecturas; este mide cuánto
 * se **despegó** la tasa Aguacate del spot en una misma lectura. Son dos
 * umbrales con fines distintos y no se comparten.
 *
 * El umbral vive acá como constante a propósito: es el paso 1, que no toca
 * backend. Cuando el módulo de tasas del Nest exponga la banda como
 * configuración, `verificarMarkup` la recibe por parámetro y no cambia nada
 * más — por eso el umbral ya es un argumento y no una lectura interna.
 */

/** Ratio máximo aceptado entre la tasa Aguacate y el spot. 1.005 = 0,5% de markup. */
export const UMBRAL_MARKUP_RATIO = 1.005;

export interface VerificacionMarkup {
  spot: number;
  aguacate: number;
  ratio: number;
  /** Umbral contra el que se comparó. Viaja con el resultado para que quien
   *  lo muestre o lo reporte no tenga que ir a buscarlo por su cuenta. */
  umbral: number;
  /**
   * Markup en % **con signo**: negativo si la tasa Aguacate quedó por debajo
   * del spot. Hoy solo se alerta hacia arriba, que es lo que pide la historia,
   * pero el signo se conserva para que agregar un piso más adelante no obligue
   * a tocar a quien consume esto.
   */
  markupPct: number;
  sobreUmbral: boolean;
  /**
   * Cuántos pesos le faltan a la tasa Aguacate para tocar el techo. Negativo
   * si ya lo pasó. Un porcentaje solo no le dice a nadie cuánto margen queda;
   * en plata sí se lee de una.
   */
  holguraClp: number;
}

/**
 * Devuelve la verificación, o `null` si falta alguno de los dos números o no
 * son valores utilizables. Nunca lanza: el chip que la consume simplemente no
 * se pinta, y el resto de la fila queda intacta.
 *
 * La condición es **estrictamente mayor**: un ratio de exactamente 1.005 está
 * dentro del umbral. Es lo que fija el escenario de borde de la historia.
 */
export function verificarMarkup(
  spot: number | null | undefined,
  aguacate: number | null | undefined,
  umbralRatio: number = UMBRAL_MARKUP_RATIO,
): VerificacionMarkup | null {
  const s = Number(spot);
  const a = Number(aguacate);

  if (!s || !a || !isFinite(s) || !isFinite(a) || s <= 0 || a <= 0) return null;

  const ratio = a / s;
  const techo = s * umbralRatio;

  return {
    spot: s,
    aguacate: a,
    ratio,
    umbral: umbralRatio,
    markupPct: (ratio - 1) * 100,
    sobreUmbral: ratio > umbralRatio,
    holguraClp: techo - a,
  };
}
